import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  /** Matches Input's helper slot, so a Select can explain itself in place. */
  helperText?: string;
  children: ReactNode;
}

/**
 * Select with a STATIC top label, not a floating one.
 *
 * A native `<select>` always shows its current value, so `:placeholder-shown`
 * never applies and a floating label would sit permanently raised. The
 * consequence for layout: a row mixing Input and Select aligns with `items-end`
 * rather than `items-start`, because the Select is taller by its label.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, helperText, children, className = "", id, required, ...props },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="mb-1.5 block text-xs font-medium text-ink-600">
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          required={required}
          className={`h-11 w-full appearance-none rounded-lg border bg-card ps-3 pe-9 text-sm text-ink-900 outline-none transition-colors
            ${error ? "border-danger focus:border-danger" : "border-line-strong focus:border-accent-500"}
            disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-subtle
            ${className}`}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
          {...props}
        >
          {children}
        </select>
        {/* ChevronDown is direction-neutral, so it needs no RTL mirroring. */}
        <ChevronDown
          className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-subtle"
          aria-hidden="true"
        />
      </div>
      {error ? (
        <p id={`${selectId}-error`} className="mt-1.5 text-xs text-danger">{error}</p>
      ) : helperText ? (
        <p id={`${selectId}-helper`} className="mt-1.5 text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
});
